/**
 * 用户状态管理 Context
 * 提供全局用户状态和认证相关方法
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { message } from 'antd';
import { login, logout, getCurrentUser, upgradeToTester } from '../services/api';

const UserContext = createContext(null);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // 从 localStorage 恢复用户状态
  useEffect(() => {
    const savedToken = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('user');

    if (savedToken && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setToken(savedToken);

        // 验证 token 是否有效
        getCurrentUser()
          .then((response) => {
            if (response.success && response.user) {
              // Token 有效，更新用户信息
              setUser(response.user);
              localStorage.setItem('user', JSON.stringify(response.user));
            }
          })
          .catch((error) => {
            // Token 无效，清空状态
            console.log('Token 已过期，清空登录状态');
            setUser(null);
            setToken(null);
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
          });
      } catch (e) {
        console.error('Failed to parse saved user:', e);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
      }
    }

    setLoading(false);
  }, []);

  // 登录
  const handleLogin = async (googleIdToken) => {
    try {
      const response = await login(googleIdToken);
      if (response.success) {
        setUser(response.user);
        setToken(response.token);
        localStorage.setItem('auth_token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        message.success('登录成功！');
        return true;
      } else {
        message.error(response.error || '登录失败');
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      message.error('登录失败，请重试');
      return false;
    }
  };

  // 登出
  const handleLogout = async () => {
    try {
      if (token) {
        await logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      message.success('已登出');
    }
  };

  // 刷新用户信息
  const refreshUser = async () => {
    if (!token) return;
    
    try {
      const response = await getCurrentUser();
      if (response.success && response.user) {
        setUser(response.user);
        localStorage.setItem('user', JSON.stringify(response.user));
      }
    } catch (error) {
      console.error('Refresh user error:', error);
      // 如果 token 失效，清除状态
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  // 升级为内测用户
  const handleUpgrade = async (accessCode) => {
    try {
      const response = await upgradeToTester(accessCode);
      if (response.success) {
        if (response.user) {
          setUser(response.user);
          localStorage.setItem('user', JSON.stringify(response.user));
        }
        message.success(response.message || '升级成功！');
        return true;
      } else {
        message.error(response.message || response.error || '升级失败');
        return false;
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      message.error('升级失败，请重试');
      return false;
    }
  };

  // 判断用户角色
  const isTester = () => user?.user_role === 'TESTER';
  const isNormal = () => user?.user_role === 'NORMAL';
  const isGuest = () => !user;

  const value = {
    user,
    token,
    loading,
    isTester,
    isNormal,
    isGuest,
    login: handleLogin,
    logout: handleLogout,
    refreshUser,
    upgrade: handleUpgrade,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

